import React, { useState, FormEvent, useEffect, useRef } from "react";
import "./DiscountForm.css";
import { toast } from "react-toastify";
import { getAllBuyers, getAllSellers } from "../../../services/Users/User.service";
import { getCategories } from "../../../services/Category/category.service";
import { createDiscount, CreateDiscountPayload, updateDiscount } from "../../../services/Discount/discount.service";
import { SellerTypes } from "../../../services/Users/User.types";
import { Category } from "../../../services/Category/category.types";
import { Tag, Calendar, Users, User, Layers, Percent, DollarSign, FileText, Search, ChevronDown, X, Check, RefreshCw, Save, Plus, Edit3 } from "lucide-react";

export type DiscountType = "percentage" | "flatAmount";
export type EntityType = "" | "buyer" | "seller" | "category";

export interface DiscountFormValues {
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  type: DiscountType;
  entityType: EntityType;
  entityValues: string[];
  value: string;
  minOrder: string;
  maxDiscount: string;
}

interface EntityOption {
  value: string;
  label: string;
  type?: string;
  firstName?: string;
  lastName?: string;
  businessName?: string;
  mobileNumber?: string;
  email?: string;
}

interface Props {
  initialValues: DiscountFormValues | null;
  submitLabel: string;
  onSubmit?: (values: DiscountFormValues) => void | Promise<void>;
  mode?: "create" | "update" | "edit";
  discountId?: number;
  onSuccess?: () => void;
}

const defaultValues: DiscountFormValues = {
  name: "",
  description: "",
  startDate: "",
  endDate: "",
  type: "percentage",
  entityType: "",
  entityValues: [],
  value: "",
  minOrder: "",
  maxDiscount: "",
};

// Format display name for dropdown - shows firstName, lastName, mobileNumber
const formatDisplayName = (item: Partial<SellerTypes>, type: 'buyer' | 'seller') => {
  const firstName = item.firstName || '';
  const lastName = item.lastName || '';
  const mobileNumber = item.mobileNumber || '';
  const businessName = item.businessName || '';
  
  let displayName = '';
  
  // For both buyer and seller, show firstName + lastName
  const namePart = [firstName, lastName].filter(Boolean).join(' ') || 'Unknown';
  
  if (type === 'seller' && businessName) {
    // For sellers: Show Business Name (First Last) - Mobile
    displayName = `${businessName} (${namePart})`;
  } else {
    // For buyers: Show First Last - Mobile
    displayName = namePart;
  }
  
  // Add mobile number if available
  if (mobileNumber) {
    displayName += ` - ${mobileNumber}`;
  }
  
  return displayName;
};

// Multi-select dropdown component with improved search
interface MultiSelectProps {
  options: EntityOption[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  placeholder: string;
  loading?: boolean;
  entityType: string;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  selectedValues,
  onChange,
  placeholder,
  loading,
  entityType,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleOption = (id: string) => {
    let newSelected: string[];
    if (selectedValues.includes(id)) {
      newSelected = selectedValues.filter((x) => x !== id);
    } else {
      newSelected = [...selectedValues, id];
    }
    onChange(newSelected);
  };

  const selectAll = () => {
    const allIds = options.map(opt => opt.value);
    onChange(allIds);
  };

  const deselectAll = () => {
    onChange([]);
  };

  const isAllSelected = options.length > 0 && selectedValues.length === options.length;

  // Enhanced search filtering - search across firstName, lastName, businessName, mobileNumber
  const filteredOptions = options.filter((opt) => {
    if (!searchTerm.trim()) return true;
    const searchLower = searchTerm.toLowerCase().trim();
    const searchRaw = searchTerm.trim();
    
    // Get all searchable fields
    const firstName = (opt.firstName || "").toLowerCase();
    const lastName = (opt.lastName || "").toLowerCase();
    const businessName = (opt.businessName || "").toLowerCase();
    const mobileNumber = opt.mobileNumber || "";
    const label = opt.label.toLowerCase();
    const fullName = [firstName, lastName].filter(Boolean).join(' ');
    
    // Search conditions - search across all fields
    return (
      label.includes(searchLower) ||
      firstName.includes(searchLower) ||
      lastName.includes(searchLower) ||
      businessName.includes(searchLower) ||
      fullName.includes(searchLower) ||
      mobileNumber.includes(searchRaw) ||
      // Remove special characters from mobile for better matching
      mobileNumber.replace(/[\s\-\(\)]/g, '').includes(searchRaw.replace(/[\s\-\(\)]/g, '')) ||
      // Search firstName + lastName together
      `${firstName} ${lastName}`.includes(searchLower) ||
      // Search businessName + firstName
      `${businessName} ${firstName}`.includes(searchLower)
    );
  });

  const selectedCount = selectedValues.length;

  return (
    <div className="multi-select" ref={containerRef}>
      <div className="multi-select__input-container" onClick={() => setIsOpen(!isOpen)}>
        <div className="multi-select__search-wrapper">
          <Search size={16} className="multi-select__search-icon" />
          <input
            type="text"
            className="multi-select__search"
            placeholder={selectedCount > 0 ? `${selectedCount} selected` : placeholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
        <div className="multi-select__right">
          {selectedCount > 0 && (
            <span className="multi-select__count">{selectedCount}</span>
          )}
          <ChevronDown size={18} className={`multi-select__arrow ${isOpen ? "open" : ""}`} />
        </div>
      </div>

      {isOpen && (
        <div className="multi-select__dropdown">
          {loading ? (
            <div className="multi-select__loading">
              <RefreshCw size={18} className="spinner" />
              Loading {entityType}s...
            </div>
          ) : options.length === 0 ? (
            <div className="multi-select__empty">No {entityType}s found</div>
          ) : (
            <>
              <div className="multi-select__dropdown-header">
                <label className="multi-select__option select-all-row">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={(e) => {
                      if (e.target.checked) selectAll();
                      else deselectAll();
                    }}
                  />
                  <span className="select-all-text">Select All</span>
                </label>
                {selectedCount > 0 && (
                  <button className="multi-select__clear" onClick={deselectAll}>
                    <X size={14} /> Clear
                  </button>
                )}
              </div>
              <div className="multi-select__divider"></div>
              
              {searchTerm && (
                <div className="multi-select__search-info">
                  Found {filteredOptions.length} {entityType}(s) matching "{searchTerm}"
                </div>
              )}
              
              <div className="multi-select__options-list">
                {filteredOptions.length === 0 ? (
                  <div className="multi-select__empty">
                    No {entityType}s found matching "<strong>{searchTerm}</strong>"
                  </div>
                ) : (
                  filteredOptions.map((opt) => (
                    <label key={opt.value} className="multi-select__option">
                      <input
                        type="checkbox"
                        checked={selectedValues.includes(opt.value)}
                        onChange={() => toggleOption(opt.value)}
                      />
                      <span className="multi-select__option-label">
                        <span className="option-name">{opt.label}</span>
                        {opt.type && (
                          <span className="multi-select__option-type">[{opt.type}]</span>
                        )}
                      </span>
                      {selectedValues.includes(opt.value) && (
                        <Check size={16} className="multi-select__check" />
                      )}
                    </label>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      )}

      {selectedCount > 0 && !isOpen && (
        <div className="multi-select__selected-preview">
          {selectedValues.slice(0, 3).map((id) => {
            const opt = options.find((o) => o.value === id);
            return opt ? (
              <span key={id} className="multi-select__preview-tag">
                {opt.label}
              </span>
            ) : null;
          })}
          {selectedCount > 3 && (
            <span className="multi-select__preview-more">+{selectedCount - 3} more</span>
          )}
        </div>
      )}
    </div>
  );
};

const DiscountForm: React.FC<Props> = ({
  initialValues,
  submitLabel,
  onSubmit,
  mode = "edit",
  discountId,
  onSuccess,
}) => {
  const [form, setForm] = useState<DiscountFormValues>(initialValues || defaultValues);
  const [buyerOptions, setBuyerOptions] = useState<EntityOption[]>([]);
  const [sellerOptions, setSellerOptions] = useState<EntityOption[]>([]);
  const [combinedBuyerSellerOptions, setCombinedBuyerSellerOptions] = useState<EntityOption[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<EntityOption[]>([]);
  const [loadingBuyers, setLoadingBuyers] = useState(false);
  const [loadingSellers, setLoadingSellers] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingCombined, setLoadingCombined] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Fetch buyers
  useEffect(() => {
    const fetchBuyers = async () => {
      try {
        setLoadingBuyers(true);
        const data: SellerTypes[] = await getAllBuyers();
        const mapped = (data || []).map((buyer) => {
          const firstName = buyer.firstName || '';
          const lastName = buyer.lastName || '';
          const mobileNumber = buyer.mobileNumber || '';
          const businessName = buyer.businessName || '';
          
          // Format: First Last - Mobile
          let label = [firstName, lastName].filter(Boolean).join(' ') || 'Unknown';
          if (mobileNumber) {
            label += ` - ${mobileNumber}`;
          }
          
          return {
            value: `buyer_${buyer.id}`,
            label: label,
            type: "Buyer",
            firstName: firstName,
            lastName: lastName,
            businessName: businessName,
            mobileNumber: mobileNumber,
            email: buyer.email || "",
          };
        });
        setBuyerOptions(mapped);
      } catch (error) {
        console.error(error);
        toast.error("Failed to load buyers");
        setBuyerOptions([]);
      } finally {
        setLoadingBuyers(false);
      }
    };
    fetchBuyers();
  }, []);

  // Fetch sellers
  useEffect(() => {
    const fetchSellers = async () => {
      try {
        setLoadingSellers(true);
        const data: SellerTypes[] = await getAllSellers();
        const mapped = (data || []).map((seller) => {
          const firstName = seller.firstName || '';
          const lastName = seller.lastName || '';
          const businessName = seller.businessName || '';
          const mobileNumber = seller.mobileNumber || '';
          
          // Format: Business Name (First Last) - Mobile
          let label = '';
          const namePart = [firstName, lastName].filter(Boolean).join(' ') || 'Unknown';
          
          if (businessName) {
            label = `${businessName} (${namePart})`;
          } else {
            label = namePart;
          }
          
          if (mobileNumber) {
            label += ` - ${mobileNumber}`;
          }
          
          return {
            value: `seller_${seller.id}`,
            label: label,
            type: "Seller",
            firstName: firstName,
            lastName: lastName,
            businessName: businessName,
            mobileNumber: mobileNumber,
            email: seller.email || "",
          };
        });
        setSellerOptions(mapped);
      } catch (error) {
        console.error(error);
        toast.error("Failed to load sellers");
        setSellerOptions([]);
      } finally {
        setLoadingSellers(false);
      }
    };
    fetchSellers();
  }, []);

  // Combine buyers and sellers when entityType is "buyer"
  useEffect(() => {
    if (form.entityType === "buyer") {
      setLoadingCombined(true);
      const combined = [...buyerOptions, ...sellerOptions];
      setCombinedBuyerSellerOptions(combined);
      setLoadingCombined(false);
    } else {
      setCombinedBuyerSellerOptions([]);
    }
  }, [form.entityType, buyerOptions, sellerOptions]);

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoadingCategories(true);
        const data: Category[] = await getCategories();
        const mapped = (data || []).map((cat) => ({
          value: `category_${cat.id}`,
          label: cat.name || "-",
          type: "Category",
        }));
        setCategoryOptions(mapped);
      } catch (error) {
        console.error(error);
        toast.error("Failed to load categories");
        setCategoryOptions([]);
      } finally {
        setLoadingCategories(false);
      }
    };
    fetchCategories();
  }, []);

  const getEntityOptions = (): EntityOption[] => {
    if (form.entityType === "buyer") {
      return combinedBuyerSellerOptions;
    }
    if (form.entityType === "seller") {
      return sellerOptions;
    }
    if (form.entityType === "category") {
      return categoryOptions;
    }
    return [];
  };

  const getLoading = () => {
    if (form.entityType === "buyer") return loadingCombined || loadingBuyers || loadingSellers;
    if (form.entityType === "seller") return loadingSellers;
    if (form.entityType === "category") return loadingCategories;
    return false;
  };

  const getEntityLabel = () => {
    if (form.entityType === "buyer") return "Buyers & Sellers";
    if (form.entityType === "seller") return "Sellers";
    if (form.entityType === "category") return "Categories";
    return "";
  };

  const getEntityIcon = () => {
    if (form.entityType === "buyer") return <Users size={18} />;
    if (form.entityType === "seller") return <User size={18} />;
    if (form.entityType === "category") return <Layers size={18} />;
    return null;
  };

  const validateDates = () => {
    if (form.startDate && form.endDate) {
      const start = new Date(form.startDate);
      const end = new Date(form.endDate);
      if (start > end) {
        toast.error("Start date must be before end date");
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!form.name || !form.endDate || !form.entityType || form.entityValues.length === 0) {
      toast.error("Please fill all required fields and select at least one entity");
      return;
    }

    if (!validateDates()) {
      return;
    }

    try {
      setSubmitting(true);

      const actualIds = form.entityValues.map(val => {
        const parts = val.split('_');
        return parseInt(parts[parts.length - 1]);
      });

      const payload: CreateDiscountPayload = {
        discountName: form.name,
        description: form.description || undefined,
        startDate: form.startDate || undefined,
        endDate: form.endDate,
        valueType: form.type === "flatAmount" ? "flatAmount" : "percentage",
        entityType: form.entityType as CreateDiscountPayload["entityType"],
        entityIds: actualIds,
        minOrderValue: form.minOrder ? Number(form.minOrder) : undefined,
        maxDiscount: form.maxDiscount ? Number(form.maxDiscount) : undefined,
      };

      let res;
      if (mode === "create") {
        res = await createDiscount(payload);
        toast.success(res?.message || "Discount created successfully");
      } else if (mode === "update" && discountId) {
        res = await updateDiscount(discountId, payload);
        toast.success(res?.message || "Discount updated successfully");
      } else {
        await onSubmit?.(form);
      }

      onSuccess?.();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="discount-form">
      {/* Header */}
      <div className="form-header">
        <div className="form-header-left">
          <div className="form-header-icon">
            <Tag size={22} />
          </div>
          <div>
            <h3>{mode === "create" ? "Create New Discount" : "Update Discount"}</h3>
            <p>Configure your discount rules and apply them to entities</p>
          </div>
        </div>
      </div>

      {/* Form Grid */}
      <div className="form-grid">
        {/* Left Column */}
        <div className="form-col">
          <div className="form-card">
            <div className="form-card-title">
              <FileText size={16} />
              Basic Information
            </div>
            <div className="field">
              <label>Discount Name <span className="required">*</span></label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g., Summer Sale 2024"
              />
            </div>
            <div className="field">
              <label>Description</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2}
                placeholder="Optional description for this discount"
              />
            </div>
          </div>

          <div className="form-card">
            <div className="form-card-title">
              <Calendar size={16} />
              Date Range
            </div>
            <div className="date-row">
              <div className="field">
                <label>Start Date <span className="required">*</span></label>
                <input
                  type="date"
                  value={form.startDate}
                  onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                />
              </div>
              <div className="field">
                <label>End Date <span className="required">*</span></label>
                <input
                  type="date"
                  value={form.endDate}
                  onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="form-col">
          <div className="form-card">
            <div className="form-card-title">
              <Percent size={16} />
              Discount Settings
            </div>
            <div className="field">
              <label>Discount Type</label>
              <div className="type-group">
                <button
                  type="button"
                  className={`type-option ${form.type === "percentage" ? "active" : ""}`}
                  onClick={() => setForm({ ...form, type: "percentage" })}
                >
                  <Percent size={16} />
                  Percentage
                </button>
                <button
                  type="button"
                  className={`type-option ${form.type === "flatAmount" ? "active" : ""}`}
                  onClick={() => setForm({ ...form, type: "flatAmount" })}
                >
                  <DollarSign size={16} />
                  Flat Amount
                </button>
              </div>
            </div>
            <div className="field">
              {/* <label>Discount Value</label>
              <input
                type="number"
                value={form.value}
                onChange={(e) => setForm({ ...form, value: e.target.value })}
                placeholder={form.type === "percentage" ? "e.g., 10" : "e.g., 50"}
              /> */}
            </div>
            <div className="field">
              <label>Minimum Order Value</label>
              <input
                type="number"
                value={form.minOrder}
                onChange={(e) => setForm({ ...form, minOrder: e.target.value })}
                placeholder="0"
              />
            </div>
            <div className="field">
              <label>Maximum Discount</label>
              <input
                type="number"
                value={form.maxDiscount}
                onChange={(e) => setForm({ ...form, maxDiscount: e.target.value })}
                placeholder="Unlimited"
              />
            </div>
          </div>

          <div className="form-card">
            <div className="form-card-title">
              {getEntityIcon() || <Users size={16} />}
              Apply To
            </div>
            <div className="field">
              <label>Entity Type <span className="required">*</span></label>
              <select
                value={form.entityType}
                onChange={(e) => setForm({ ...form, entityType: e.target.value as EntityType, entityValues: [] })}
              >
                <option value="">Select Entity Type</option>
                <option value="buyer">👥 Buyers & Sellers</option>
                <option value="seller">👤 Sellers Only</option>
                <option value="category">📂 Categories</option>
              </select>
            </div>

            {form.entityType && (
              <div className="field">
                <label>
                  {getEntityIcon()}
                  Select {getEntityLabel()} <span className="required">*</span>
                </label>
                <MultiSelect
                  options={getEntityOptions()}
                  selectedValues={form.entityValues}
                  onChange={(values) => setForm({ ...form, entityValues: values })}
                  placeholder="Search by name, business, or mobile..."
                  loading={getLoading()}
                  entityType={form.entityType}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="form-actions">
        <button
          type="button"
          className="btn-secondary"
          onClick={() => setForm(initialValues || defaultValues)}
        >
          Reset
        </button>
        <button
          type="submit"
          className="btn-primary"
          disabled={submitting}
        >
          {submitting ? (
            <>
              <RefreshCw size={18} className="spinner" />
              Saving...
            </>
          ) : (
            <>
              <Save size={18} />
              {submitLabel}
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default DiscountForm;